import { useEffect, useMemo, useRef } from 'react';

import { userPaths } from 'routes/constants';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';

export const FROM_NO_EXPENSE_FOUND_STATE_KEY = 'fromNoExpenseFound';
export const FROM_END_USER_DASHBOARD_STATE_KEY = 'fromEndUserDashboard';
export const OTP_VERIFIED_LOCK_KEY = 'spence_onboarding_otp_verified';
export { FIND_EXPENSES_COMPLETED_LOCK_KEY } from 'utils/findExpensesCompletedLock';
export const RESET_OTP_VERIFIED_LOCK_STATE_KEY = 'resetOtpVerifiedLock';

import { Box } from '@mui/material';

import Stepper from 'components/stepper';

import { isEndUserTpaFlowActive } from 'utils/endUserTpaFlowStorage';
import { isFindExpensesCompletedLockSet } from 'utils/findExpensesCompletedLock';
import { readStoredEuId } from 'utils/onboardingInvitation';

import logoImage from 'assets/images/Logo.png';

import { OnboardingChrome, OnboardingLogo, OnboardingShell } from 'styles/onboardingLayout';

/** Figma onboarding stepper — single-line labels aligned to the screen content edges. */
const ONBOARDING_STEPS = ['Verify Identity', 'Set Up Account', 'Find Expenses'];

const PATH_TO_STEP: Record<string, number> = {
  [userPaths.onboardingEmailVerification]: 0,
  [userPaths.onboardingPhoneVerification]: 0,
  [userPaths.onboardingOtpVerification]: 0,
  [userPaths.onboardingCompleteProfile]: 1,
  [userPaths.onboardingPersonalDetails]: 1,
  [userPaths.onboardingFindExpenses]: 2,
};

const OTP_LOCKED_ROUTES = new Set<string>([
  userPaths.onboardingEmailVerification,
  userPaths.onboardingPhoneVerification,
  userPaths.onboardingOtpVerification,
]);
const POST_OTP_ROUTE_PATHS = new Set<string>([
  userPaths.onboardingCompleteProfile,
  userPaths.onboardingPersonalDetails,
  userPaths.onboardingFindExpenses,
]);

const isPostOtpRoute = (pathname: string): boolean => POST_OTP_ROUTE_PATHS.has(pathname);

const isFindExpensesDashboardEntry = (
  pathname: string,
  fromNoExpenseFound: unknown,
  fromEndUserDashboard: unknown
): boolean =>
  pathname.startsWith(userPaths.onboardingFindExpenses) &&
  (fromNoExpenseFound === true || fromEndUserDashboard === true || isEndUserTpaFlowActive());

const getActiveStep = (pathname: string): number => {
  const exact = PATH_TO_STEP[pathname];
  if (exact !== undefined) return exact;
  return 0;
};

/** Stepper is only visible up to the Connect Health Insurance screen. */
const isStepperVisibleByPath = (pathname: string): boolean =>
  !pathname.startsWith(userPaths.onboardingFindExpensesProviders) &&
  pathname !== userPaths.onboardingFindExpensesSuccess;

const OnboardingLayout = () => {
  const navigate = useNavigate();
  const { pathname, state, search, hash } = useLocation();
  const currentOnboardingPathRef = useRef<string>(userPaths.onboardingEmailVerification);
  const activeStep = useMemo(() => getActiveStep(pathname), [pathname]);
  const stateObj = state as Record<string, unknown> | null;
  const fromNoExpenseFound =
    stateObj != null ? stateObj[FROM_NO_EXPENSE_FOUND_STATE_KEY] : undefined;
  const fromEndUserDashboard =
    stateObj != null ? stateObj[FROM_END_USER_DASHBOARD_STATE_KEY] : undefined;
  const isDashboardFindExpensesEntry = isFindExpensesDashboardEntry(
    pathname,
    fromNoExpenseFound,
    fromEndUserDashboard
  );
  const shouldResetOtpLockFromEntry =
    stateObj != null ? stateObj[RESET_OTP_VERIFIED_LOCK_STATE_KEY] === true : false;
  const hideStepperOnFindExpenses =
    pathname === userPaths.onboardingFindExpenses && isDashboardFindExpensesEntry;
  const showStepper = isStepperVisibleByPath(pathname) && !hideStepperOnFindExpenses;
  // Find Expenses connect screen only — drop grey top/bottom bands (Figma is all white).
  const useWhiteSurface = pathname === userPaths.onboardingFindExpenses;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Invitation-only: public identity steps require a resolved referral euId.
    if (OTP_LOCKED_ROUTES.has(pathname) && !readStoredEuId()) {
      navigate(userPaths.ftue, { replace: true });
      return;
    }

    const isFindExpensesCompleted = isFindExpensesCompletedLockSet();
    if (
      isFindExpensesCompleted &&
      pathname.startsWith(userPaths.onboarding) &&
      !isDashboardFindExpensesEntry
    ) {
      navigate(userPaths.noExpenseFound, { replace: true });
      return;
    }

    if (shouldResetOtpLockFromEntry) {
      window.sessionStorage.removeItem(OTP_VERIFIED_LOCK_KEY);
    }

    const isOtpLocked = window.sessionStorage.getItem(OTP_VERIFIED_LOCK_KEY) === 'true';

    if (isOtpLocked && OTP_LOCKED_ROUTES.has(pathname)) {
      navigate(userPaths.onboardingCompleteProfile, { replace: true });
      return;
    }

    if (!isOtpLocked && isPostOtpRoute(pathname) && !isDashboardFindExpensesEntry) {
      navigate(userPaths.onboardingEmailVerification, { replace: true });
    }
  }, [isDashboardFindExpensesEntry, navigate, pathname, shouldResetOtpLockFromEntry]);

  useEffect(() => {
    const fullPath = `${pathname}${search}${hash}`;
    currentOnboardingPathRef.current = fullPath;

    if (typeof window === 'undefined') {
      return undefined;
    }

    window.history.pushState({ onboardingGuard: true }, '', fullPath);

    const onPopState = () => {
      const guardedPath = currentOnboardingPathRef.current;
      navigate(guardedPath, { replace: true });
      window.history.pushState({ onboardingGuard: true }, '', guardedPath);
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [hash, navigate, pathname, search]);

  return (
    <OnboardingShell $whiteSurface={useWhiteSurface}>
      {showStepper && (
        <OnboardingChrome $whiteSurface={useWhiteSurface}>
          <Stepper steps={ONBOARDING_STEPS} activeStep={activeStep} />
          <OnboardingLogo>
            <img src={logoImage} alt="Spence Logo" />
          </OnboardingLogo>
        </OnboardingChrome>
      )}
      <Box sx={{ flex: 1 }}>
        <Outlet />
      </Box>
    </OnboardingShell>
  );
};

export default OnboardingLayout;
